import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import { setDropModalOpen } from 'redux/actions';
import { selectUserPreferences } from 'redux/selectors';
import { selectUserCategoryPreferences, selectUserPreference } from 'redux/UserPreferences';
import { MoreIcon, AddCircleIcon, SettingsIcon } from 'components/icons';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import DropModal from 'components/shared/DropModal/DropModal';
import { ProgressIndicator } from 'components/shared/Progress';
import { fallbackByCategory } from '../../constants';
import styles from './ChartSetPreferenceDropdown.module.css';

const DROPDOWN_NAME = 'selectChartSet';
const fallbackIds = fallbackByCategory[USER_PREFERENCE_SETS.CHART_SET];

function ChartSetPreferenceDropdown({ preference, onSave, onSelect, onManage }) {
  const groupTitleId = useMemo(() => v4(), []);
  const dispatch = useDispatch();
  const { loading, ids } = useSelector((state) =>
    selectUserCategoryPreferences(selectUserPreferences(state), USER_PREFERENCE_SETS.CHART_SET)
  );
  const closeDropdown = useCallback(() => {
    dispatch(setDropModalOpen(DROPDOWN_NAME, false));
  }, [dispatch]);
  const handleClickSave = useCallback(() => {
    closeDropdown();
    onSave();
  }, [closeDropdown, onSave]);
  const handleSelect = useCallback(
    (preferenceId) => {
      closeDropdown();
      onSelect(preferenceId);
    },
    [closeDropdown, onSelect]
  );
  const handleClickManage = useCallback(() => {
    closeDropdown();
    onManage();
  }, [closeDropdown, onManage]);

  return (
    <DropModal
      data-testid="chart-set-preference-select"
      name={DROPDOWN_NAME}
      title={<MoreIcon size="medium" className={styles.more} />}
      className={styles.popup}
    >
      <div role="menu" aria-label="Chart set preferences" aria-busy={loading} className={styles.menu}>
        <PerfectScrollbar>
          {loading && (
            <div className={styles.progressbar}>
              <ProgressIndicator />
            </div>
          )}
          <div className={styles.menuList}>
            <div role="menuitem" className={styles.menuItem} onClick={handleClickSave}>
              <AddCircleIcon className={styles.menuItemIcon} />
              Save chart set as
            </div>
            <div role="separator" className={styles.menuSeparator} />
            <div role="group" aria-labelledby={groupTitleId}>
              <div id={groupTitleId} className={styles.menuSubHeader}>
                Choose from your saved chart sets
              </div>
              {fallbackIds.map((id) => (
                <ChartSetMenuItem
                  key={id}
                  preferenceId={id}
                  checked={id === preference?.preferenceId}
                  onClick={handleSelect}
                />
              ))}
              {ids.map((id) => (
                <ChartSetMenuItem
                  key={id}
                  preferenceId={id}
                  checked={id === preference?.preferenceId}
                  onClick={handleSelect}
                />
              ))}
            </div>
            <div role="separator" className={styles.menuSeparator} />
            <div role="menuitem" className={styles.menuItem} onClick={handleClickManage}>
              <SettingsIcon className={styles.menuItemIcon} />
              Manage chart sets
            </div>
          </div>
        </PerfectScrollbar>
      </div>
    </DropModal>
  );
}

export default ChartSetPreferenceDropdown;

function ChartSetMenuItem({ preferenceId, checked, onClick }) {
  const preference = useSelector((state) => selectUserPreference(selectUserPreferences(state), preferenceId));
  const handleClick = () => {
    onClick(preferenceId);
  };

  return (
    <div role="menuitemradio" aria-checked={checked} className={styles.menuItem} onClick={handleClick}>
      {preference.name}
      {preference.local ? ' (System Default)' : ''}
    </div>
  );
}
